import { useContext, useEffect, useRef } from "react";
import notificationSound from "../Sound/order_Update_notification.mp3";
import paymentSound from "../Sound/payment_received.mp3";
import { toast } from "react-toastify";
import { GlobalContext } from "../Context/GlobalContext";

const OrderUpdateWebSocket = ({ onOrderUpdate }) => {
  const { userID } = useContext(GlobalContext);

  const audioRef = useRef(null);
  // Initialize audio once
  useEffect(() => {
    const audio = new Audio(notificationSound);
    audio.preload = "auto";
    audio.loop = true;
    audio.volume = 1.0;
    audioRef.current = audio;

    // Unlock audio on first user interaction
    const unlockAudio = () => {
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.muted = false;
          localStorage.setItem("soundUnlocked", "true");
          console.log("🔓 Audio unlocked");
        })
        .catch((err) => {
          console.warn("Unlock failed:", err);
        });

      document.removeEventListener("click", unlockAudio);
      document.removeEventListener("mousemove", unlockAudio);
    };

    if (localStorage.getItem("soundUnlocked") !== "true") {
      document.addEventListener("click", unlockAudio, { once: true });
      document.addEventListener("mousemove", unlockAudio, { once: true });
    }

    return () => {
      document.removeEventListener("click", unlockAudio);
      document.removeEventListener("mousemove", unlockAudio);
    };
  }, []);

  useEffect(() => {
    if (!userID) {
      console.error("userID is missing. Order update WebSocket not started.");
      return;
    }

    let uniqueId = sessionStorage.getItem("uniqueId");
    if (!uniqueId) {
      uniqueId = crypto.randomUUID();
      sessionStorage.setItem("uniqueId", uniqueId);
    }

    let socket;

    const connectWebSocket = () => {
      console.log(" Connecting to Order Update WebSocket with userID:", userID);

      socket = new WebSocket(
        `wss://api.anlook.com/ws?userId=${uniqueId}&roomId=${userID}_order_update_master_v1`,
      );

      socket.onopen = () => {
        console.log(" Order Update WebSocket Connected");
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          console.log(" Parsed Order Update:", data);

          //  Smart update handling
          if (data.Message?.includes("Payment Received For Order")) {
            setTimeout(() => {
              onOrderUpdate();

              window.dispatchEvent(new Event("orderUpdated"));
            }, 1500);
          } else {
            onOrderUpdate();

            window.dispatchEvent(new Event("orderUpdated"));
          }

          toast.info(data.Message, {
            position: "top-right",
            autoClose: 4000,
          });

          // 🔊 Sound logic
          let soundToPlay = null;

          if (data.Message?.includes("Payment Received For Order")) {
            soundToPlay = new Audio(paymentSound);
          } else if (data.Message?.includes("Has been acknowledged")) {
            soundToPlay = new Audio(notificationSound);
          }

          if (soundToPlay) {
            soundToPlay.play().catch(() => {});
          }
        } catch (error) {
          console.error(" Error parsing:", error);
        }
      };

      socket.onerror = (error) => {
        console.error(" Order Update WebSocket Error:", error);
      };
      socket.onclose = (event) => {
        console.warn(
          " Order Update WebSocket Closed:",
          event.reason,
          "Code:",
          event.code,
        );
        // Reconnect when WebSocket gets closed
        console.log("♻️ Reconnecting Order Update WebSocket...");
        connectWebSocket(); // Reconnect on closure
      };
    };

    connectWebSocket(); // Initial connection

    // Cleanup function when the component unmounts
    return () => {
      if (socket) socket.close();
    };
  }, [userID]);

  return null;
};

export default OrderUpdateWebSocket;
